/* eslint-env browser */
// Finance view over /event_payments (live-event access fees), wave 3 report
// look: two tiles, the event filter as a chip, the modern table, and the
// Manual entry dialog. Records are denormalized at payment time (payer
// name/mobile/usertype are written by the Chapa callback), so this page
// renders straight from one small node — no join against /users.
//
// Reads and writes are the old page's: fetchEventPayments() on open and on
// Refresh; Manual entry calls the manual_event_payment function with
// { action: 'lookup', mobile } and then { action: 'approve', uid, amount,
// transaction_id, note } (amount as typed, as before), after a confirmation
// that names the person, the event and the amount.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it is not there while
// numbers are hidden.
import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { api, FirebaseContext } from 'common';
import DashboardCard from '../components/DashboardCard';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import {
  DataTable,
  PageHeader,
  DropChip,
  ActiveFilters,
  CopyButton,
  FieldLabel,
  FieldError,
  SrOnly,
  ALL,
  formatMoney,
  formatNumber,
  formatPhoneDisplay,
  toNineDigits,
  toEthiopianE164,
  isNineDigitMobile,
  phoneMatches,
} from '../components/modern';
import { tokens as c } from '../theme';

export default function EventPayments() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector((state) => state.settingsdata.settings);
  const { auth: firebaseAuth, config } = useContext(FirebaseContext);
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);

  const [payments, setPayments] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [eventFilter, setEventFilter] = useState(ALL);

  // Manual-entry dialog: for payments Chapa charged but whose callback
  // never landed. Lookup is a single phone-number query server-side —
  // never a full users download.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMobile, setManualMobile] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [manualTxn, setManualTxn] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [approving, setApproving] = useState(false);
  const [manualError, setManualError] = useState('');
  const [mobileTouched, setMobileTouched] = useState(false);

  const loadPayments = useCallback(() => {
    setRefreshing(true);
    api
      .fetchEventPayments()
      .then((val) => setPayments(val || {}))
      .catch(() => setPayments({}))
      .finally(() => setRefreshing(false));
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const callManualApi = useCallback(
    async (payload) => {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) throw new Error('not_signed_in');
      const idToken = await currentUser.getIdToken();
      const host =
        typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin
          ? window.location.origin
          : `https://${config?.projectId || 'donkey-drive-5cdd1'}.web.app`;
      const res = await fetch(`${host}/manual_event_payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `request_failed_${res.status}`);
      return json;
    },
    [firebaseAuth, config, settings]
  );

  const manualErrorText = (code) => {
    switch (code) {
      case 'no_active_event':
        return t('event_manual_no_event', { defaultValue: 'No active event is configured in Settings.' });
      case 'already_paid':
        return t('event_manual_already', { defaultValue: 'This user already has access for the current event.' });
      case 'user_not_found':
        return t('event_manual_not_found', { defaultValue: 'No user found with that phone number.' });
      case 'invalid_amount':
        return t('live_event_fields_required', { defaultValue: 'Event ID, date, fee and stream URL are all required to enable the live event.' });
      default:
        return t('live_event_load_error', { defaultValue: 'Could not load event details. Please try again.' });
    }
  };

  const openManual = () => {
    setManualMobile('');
    setMobileTouched(false);
    setLookupResult(null);
    setManualAmount('');
    setManualTxn('');
    setManualNote('');
    setManualError('');
    setManualOpen(true);
  };
  const closeManual = () => {
    if (!approving) setManualOpen(false);
  };

  const mobileValid = isNineDigitMobile(manualMobile);
  const doLookup = async () => {
    setMobileTouched(true);
    if (!mobileValid || lookupLoading) return;
    setLookupLoading(true);
    setManualError('');
    setLookupResult(null);
    try {
      // +251 and the nine digits; the function also tries the 0… form.
      const result = await callManualApi({ action: 'lookup', mobile: toEthiopianE164(manualMobile) });
      setLookupResult(result);
      if (result.found) {
        setManualAmount(String(result.eventFee || ''));
      }
    } catch (err) {
      setManualError(manualErrorText(err.message));
    } finally {
      setLookupLoading(false);
    }
  };

  const personName = (r) => (r ? `${r.firstName || ''} ${r.lastName || ''}`.trim() : '');
  const canApprove = !!lookupResult?.found && !lookupResult?.alreadyPaid && parseFloat(manualAmount) > 0 && !approving;

  const doApprove = async () => {
    if (!lookupResult?.found || lookupResult.alreadyPaid || approving) return;
    const ok = await confirmDialog({
      title: t('event_manual_confirm_title', {
        name: personName(lookupResult) || '-',
        defaultValue: 'Give {{name}} access?',
      }),
      body: t('event_manual_confirm_body', {
        event: lookupResult.eventName || lookupResult.eventId,
        amount: formatMoney(parseFloat(manualAmount) || 0, settings, true),
        defaultValue: 'This records a payment of {{amount}} for {{event}}.',
      }),
      confirmLabel: t('event_manual_approve', { defaultValue: 'Approve Access' }),
      cancelLabel: t('cancel', { defaultValue: 'Cancel' }),
    });
    if (!ok) return;
    setApproving(true);
    setManualError('');
    try {
      await callManualApi({
        action: 'approve',
        uid: lookupResult.uid,
        amount: manualAmount,
        transaction_id: manualTxn.trim() || null,
        note: manualNote.trim() || null,
      });
      setManualOpen(false);
      showToast.success(t('event_manual_done', { defaultValue: 'Access approved' }));
      loadPayments();
    } catch (err) {
      setManualError(manualErrorText(err.message));
    } finally {
      setApproving(false);
    }
  };

  const rows = useMemo(() => {
    if (!payments) return [];
    const list = [];
    Object.keys(payments).forEach((eventId) => {
      const perUser = payments[eventId] || {};
      Object.keys(perUser).forEach((uid) => {
        const rec = perUser[uid] || {};
        list.push({
          eventId: eventId,
          uid: uid,
          paidAt: rec.paidAt || 0,
          customer_name: rec.customer_name || '',
          customer_contact: rec.customer_contact || '',
          customer_email: rec.customer_email || '',
          usertype: rec.usertype || '',
          order_id: rec.order_id || '',
          transaction_id: rec.transaction_id || '',
          amount: Number(rec.amount || 0),
          currency: rec.currency || '',
          source: rec.manual ? t('event_manual_label', { defaultValue: 'Manual' }) + (rec.approvedByName ? ` — ${rec.approvedByName}` : '') : 'Chapa',
        });
      });
    });
    return list;
  }, [payments, t]);

  const eventIds = useMemo(() => Object.keys(payments || {}), [payments]);
  const filteredRows = useMemo(() => (eventFilter === ALL ? rows : rows.filter((r) => r.eventId === eventFilter)), [rows, eventFilter]);
  const totalAmount = useMemo(() => filteredRows.reduce((sum, r) => sum + r.amount, 0), [filteredRows]);

  const hiddenText = t('hidden_demo', { defaultValue: 'Hidden for Demo' });
  const contactOf = (row) => (row.customer_contact ? (allowPrivate ? row.customer_contact : hiddenText) : '');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });

  const columns = [
    { title: t('date', { defaultValue: 'Date' }), field: 'paidAt', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('name', { defaultValue: 'Name' }),
      field: 'customer_name',
      render: (row) => (row.customer_name ? <Box component="span" sx={{ fontWeight: 600 }}>{row.customer_name}</Box> : null),
    },
    {
      title: t('mobile', { defaultValue: 'Mobile' }),
      field: 'customer_contact',
      nowrap: true,
      render: (row) =>
        row.customer_contact ? (
          allowPrivate ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              {formatPhoneDisplay(row.customer_contact)}
              <CopyButton value={row.customer_contact} phone what={phoneWord} sx={{ my: -0.5 }} />
            </Box>
          ) : (
            hiddenText
          )
        ) : null,
      sortable: allowPrivate,
      searchable: false,
      exportValue: contactOf,
    },
    { title: t('usertype', { defaultValue: 'User Type' }), field: 'usertype' },
    { title: t('amount', { defaultValue: 'Amount' }), field: 'amount', type: 'money', withSymbol: true },
    { title: t('live_event_order_id', { defaultValue: 'Order ID' }), field: 'order_id' },
    { title: t('transaction_id', { defaultValue: 'Transaction ID' }), field: 'transaction_id' },
    { title: t('event_payment_source', { defaultValue: 'Source' }), field: 'source' },
    { title: t('live_event', { defaultValue: 'Live Event' }), field: 'eventId' },
  ];

  const eventOptions = [{ value: ALL, label: t('all', { defaultValue: 'All' }) }].concat(
    eventIds.map((id) => ({ value: id, label: id, count: rows.filter((r) => r.eventId === id).length }))
  );
  const filterTokens = eventFilter !== ALL ? [{ key: 'event', group: t('live_event', { defaultValue: 'Live Event' }), value: eventFilter }] : [];
  const loading = payments === null;
  const mobileError = mobileTouched && manualMobile && !mobileValid ? t('form_phone_invalid', { defaultValue: 'Enter 9 digits, like 912345678' }) : '';

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        secondaryActions={[
          { key: 'refresh', label: t('refresh', { defaultValue: 'Refresh' }), icon: <RefreshRoundedIcon />, onClick: loadPayments, disabled: refreshing },
        ]}
        primaryAction={{ label: t('event_manual_entry', { defaultValue: 'Manual Entry' }), icon: <PersonAddAltRoundedIcon />, onClick: openManual }}
      />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
        <DashboardCard
          icon={<ReceiptLongOutlinedIcon />}
          hue="info"
          title={t('event_payments_count', { defaultValue: 'Payments' })}
          value={formatNumber(filteredRows.length)}
          loading={loading}
        />
        <DashboardCard
          icon={<AccountBalanceOutlinedIcon />}
          hue="success"
          title={t('event_payments_total', { defaultValue: 'Total Collected' })}
          value={formatMoney(totalAmount, settings, true)}
          loading={loading}
        />
      </Box>

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <DropChip label={t('live_event', { defaultValue: 'Live Event' })} value={eventFilter} onChange={setEventFilter} options={eventOptions} />
      </Paper>

      <DataTable
        title={t('event_payments_title', { defaultValue: 'Event Payments' })}
        rows={filteredRows}
        columns={columns}
        rowKey={(row) => `${row.eventId}/${row.uid}`}
        loading={loading}
        refreshing={refreshing && !loading}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('event_payments_rows', {
                  count: total,
                  defaultValue_one: '{{count}} payment',
                  defaultValue_other: '{{count}} payments',
                  defaultValue: '{{count}} payments',
                })
        }
        headerContent={
          filterTokens.length ? (
            <ActiveFilters tokens={filterTokens} onRemove={() => setEventFilter(ALL)} onClearAll={() => setEventFilter(ALL)} />
          ) : null
        }
        searchPlaceholder={t('event_payments_search', { defaultValue: 'Search name, phone, order or transaction' })}
        searchFn={(row, q, ql) =>
          [row.customer_name, row.usertype, row.order_id, row.transaction_id, row.source, row.eventId].some((v) => String(v || '').toLowerCase().includes(ql)) ||
          (allowPrivate && phoneMatches(row.customer_contact, q))
        }
        rowLabel={(row) => row.customer_name || row.order_id}
        prefsKey="event_payments"
        defaultPageSize={25}
        exportCsv
        exportPrint
        exportFileName="event_payments"
        exportTitle={t('event_payments_title', { defaultValue: 'Event Payments' })}
        minWidth={1180}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <LiveTvOutlinedIcon />,
                title:
                  eventFilter !== ALL
                    ? t('event_payments_empty_event', { defaultValue: 'No payments for this event' })
                    : t('event_payments_empty', { defaultValue: 'No event payments yet' }),
                actionLabel: eventFilter !== ALL ? t('clear_filters', { defaultValue: 'Clear filters' }) : undefined,
                onAction: eventFilter !== ALL ? () => setEventFilter(ALL) : undefined,
              }
        }
      />

      <Dialog open={manualOpen} onClose={closeManual} fullWidth maxWidth="sm" fullScreen={phone} aria-labelledby="ep-manual-title">
        <DialogTitle id="ep-manual-title" sx={{ pr: 8 }}>
          {t('event_manual_entry', { defaultValue: 'Manual Entry' })}
          <IconButton
            aria-label={t('close_dialog', { defaultValue: 'Close' })}
            onClick={closeManual}
            disabled={approving}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Typography variant="body2" sx={{ color: c.INK_2 }}>
            {t('event_manual_hint', {
              defaultValue: "For payments Chapa processed but that failed to record. Enter the customer's phone number to find their account.",
            })}
          </Typography>
          <Box>
            <FieldLabel htmlFor="ep-manual-mobile">{t('mobile', { defaultValue: 'Mobile' })}</FieldLabel>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                id="ep-manual-mobile"
                fullWidth
                type="tel"
                value={manualMobile}
                error={!!mobileError}
                placeholder="912345678"
                onChange={(e) => {
                  setManualMobile(toNineDigits(e.target.value));
                  setLookupResult(null);
                }}
                onBlur={() => setMobileTouched(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') doLookup();
                }}
                inputProps={{ inputMode: 'numeric', maxLength: 9, autoComplete: 'off', 'aria-describedby': mobileError ? 'ep-manual-mobile-error' : undefined }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: c.INK_2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        +251
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                color="neutral"
                onClick={doLookup}
                disabled={lookupLoading || !manualMobile}
                startIcon={lookupLoading ? <CircularProgress size={16} color="inherit" /> : <SearchRoundedIcon />}
                sx={{ minWidth: 100, minHeight: 44, flex: 'none' }}
              >
                {t('event_manual_find', { defaultValue: 'Find' })}
              </Button>
            </Box>
            <FieldError id="ep-manual-mobile-error">{mobileError}</FieldError>
          </Box>

          {manualError ? <InlineMessage kind="error">{manualError}</InlineMessage> : null}

          {lookupResult && !lookupResult.found ? (
            <InlineMessage kind="warning">{t('event_manual_not_found', { defaultValue: 'No user found with that phone number.' })}</InlineMessage>
          ) : null}

          {lookupResult?.found ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, bgcolor: c.SURFACE_ALT }}>
                <Typography sx={{ fontWeight: 700, color: c.INK }}>{personName(lookupResult) || '-'}</Typography>
                <Typography variant="body2" sx={{ color: c.INK_2, overflowWrap: 'anywhere' }}>
                  {allowPrivate ? formatPhoneDisplay(lookupResult.mobile) : hiddenText} · {lookupResult.usertype || '-'}
                  {lookupResult.email ? ` · ${allowPrivate ? lookupResult.email : hiddenText}` : ''}
                </Typography>
              </Paper>

              {lookupResult.alreadyPaid ? (
                <InlineMessage kind="info">{t('event_manual_already', { defaultValue: 'This user already has access for the current event.' })}</InlineMessage>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: c.INK }}>
                    {t('event_manual_grants', { defaultValue: 'Grants access for' })}:{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {lookupResult.eventName || lookupResult.eventId}
                    </Box>
                  </Typography>
                  <Box>
                    <FieldLabel htmlFor="ep-manual-amount">{t('amount', { defaultValue: 'Amount' })}</FieldLabel>
                    <TextField
                      id="ep-manual-amount"
                      fullWidth
                      value={manualAmount}
                      onChange={(e) => setManualAmount(e.target.value)}
                      inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box component="span" sx={{ color: c.INK_2, fontWeight: 600 }}>
                              {settings?.symbol || ''}
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
                    />
                  </Box>
                  <Box>
                    <FieldLabel htmlFor="ep-manual-txn">{t('transaction_id', { defaultValue: 'Transaction ID' })}</FieldLabel>
                    <TextField
                      id="ep-manual-txn"
                      fullWidth
                      value={manualTxn}
                      onChange={(e) => setManualTxn(e.target.value)}
                      inputProps={{ autoComplete: 'off', 'aria-describedby': 'ep-manual-txn-hint' }}
                    />
                    <SrOnly id="ep-manual-txn-hint">
                      {t('event_manual_txn_help', { defaultValue: 'Optional — the reference from the Chapa dashboard.' })}
                    </SrOnly>
                  </Box>
                  <Box>
                    <FieldLabel htmlFor="ep-manual-note">{t('event_manual_note', { defaultValue: 'Note' })}</FieldLabel>
                    <TextField id="ep-manual-note" fullWidth value={manualNote} onChange={(e) => setManualNote(e.target.value)} inputProps={{ autoComplete: 'off' }} />
                  </Box>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={closeManual} disabled={approving}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            onClick={doApprove}
            disabled={!canApprove}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('event_manual_approve', { defaultValue: 'Approve Access' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
